import { useState } from 'react';
import { BookmarkIcon, BotIcon, DatabaseIcon, FileKeyIcon, FileTextIcon, HashIcon, Link2Icon, MessageSquareIcon, PlusIcon, SparklesIcon, UserIcon, WandSparklesIcon } from 'lucide-react';
import { activityFor, attentionCountFor, chatsFor, linkedFor, statePlayFor, type ActivityEntry, type ActivityTag, type LinkedCategory } from '../../data/trackDetail';
import { avatarHue, type Hue } from '../alt/hues';
import { EmptyState, Glyph, MetaStat, Panel, Row, RowText, SearchField, SelectPill } from '../dashboard/ui';

/**
 * The three non-chat track views. Every neutral here is a semantic --app-*
 * token (see components/dashboard/ui.tsx): near-white canvas, white
 * surfaces, neutral hairlines. Colour appears only where it carries meaning
 * — the group's own hue, and the event-type legend dots.
 */

export type TrackDetailViewProps = {
  trackName: string;
  group: string;
  groupHue: Hue;
};

// Legend-style dot + label rather than a filled badge: a row of solid pills
// read as busy the moment more than one tag applied. These are the shared
// status tokens, so "issue" is the same red everywhere in the product.
const TAG_DOT: Record<ActivityTag, string> = {
  Discussion: 'var(--app-info)',
  Decision: 'var(--app-success)',
  Issue: 'var(--app-danger)',
  Progress: '#6D4CBF',
  'Customer signal': 'var(--app-warning)',
  Metric: '#4159A6'
};
const CATEGORY_ICON: Record<string, typeof BookmarkIcon> = {
  bookmarks: BookmarkIcon,
  prompts: WandSparklesIcon,
  skills: SparklesIcon,
  credentials: FileKeyIcon,
  databases: DatabaseIcon,
  slack: HashIcon,
  chats: MessageSquareIcon,
  context: FileTextIcon
};
export function TrackOverviewView({
  trackName,
  group,
  groupHue
}: TrackDetailViewProps) {
  const activity = activityFor(trackName);
  const recent = activity.flatMap(day => day.entries).slice(0, 5);
  const chats = chatsFor(trackName);
  const linked = linkedFor(trackName);
  const allLinked = [...linked.left, ...linked.right];
  const context = allLinked.find(category => category.id === 'context')?.items ?? [];
  const primaryChannel = allLinked.find(category => category.id === 'slack')?.items[0];
  return <div className="mx-auto grid w-full max-w-[1040px] grid-cols-1 gap-x-8 gap-y-8 xl:grid-cols-12" data-id="element-3856" data-mp="39vpn68sdrohw">
      <section className="xl:col-span-7" data-id="element-3857" data-mp="3esfzcl12dzy1">
        {/* ONE headline, set directly on the canvas rather than boxed — the
         content is the subject, not a card containing it. */}
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-3858" data-mp="3ojsq19b4mk2h">
          <h2 className="text-[17px] font-semibold text-app" data-id="element-3859" data-mp="3skw71z4wum5a">State of play</h2>
          <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-semibold" style={{
          backgroundColor: groupHue.tint,
          color: groupHue.ink
        }} data-id="element-3860" data-mp="1f4k18mfjpmp">
            <span className="size-1.5 rounded-full" style={{
            backgroundColor: groupHue.solid
          }} data-id="element-3861" data-mp="6cczv2k3vhks" />
            {group}
          </span>
        </div>
        <p className="mt-2.5 max-w-[66ch] text-[15px] leading-relaxed text-app-muted" data-id="element-3862" data-mp="2875ai3tuxm5l">{statePlayFor(trackName)}</p>
        <dl className="mt-5 grid grid-cols-3 gap-4 border-t border-app-divider pt-4" data-id="element-3863" data-mp="20o4fy811b2ud">
          <MetaStat label="Active chats" value={chats.length} data-id="element-3864" data-mp="1vv2wrntsoh4z" />
          <MetaStat label="Recent events" value={recent.length} data-id="element-3865" data-mp="1qwi6oh89bdu9" />
          <MetaStat label="Needs review" value={attentionCountFor(trackName)} data-id="element-3866" data-mp="1qunm540wdnqp" />
        </dl>

        <h3 className="mb-2.5 mt-7 text-[13px] font-semibold text-app-muted" data-id="element-3867" data-mp="1lzi0m6beqb35">Observations</h3>
        <Panel data-id="element-3868" data-mp="2jkntrams9ex9">
          {recent.map((entry, index) => <Row key={`${entry.time}-${index}`} data-id="element-3869" data-mp="2emm1bz3gw394" data-mpi={index}>
              <Glyph icon={entry.author === 'bot' ? BotIcon : UserIcon} data-id="element-3870" data-mp="2en43tdgq3dph" />
              <RowText title={entry.text} meta={entry.time} data-id="element-3871" data-mp="29m8296jug3n8" />
              <TagLegend tag={entry.tags[0]} data-id="element-3872" data-mp="24o6fobn7lm70" />
            </Row>)}
        </Panel>
      </section>

      <section className="xl:col-span-5" data-id="element-3873" data-mp="y82wc802ljp9">
        <h3 className="mb-2.5 text-[13px] font-semibold text-app-muted" data-id="element-3874" data-mp="y95nmh1b1o5p">Chats</h3>
        <Panel data-id="element-3875" data-mp="t8q8pl1dk8lm">
          {chats.map(chat => <Row key={chat.ref} data-id="element-3876" data-mp="t7ga8uqm0yeg">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{
            backgroundColor: avatarHue(chat.initials)
          }} data-id="element-3877" data-mp="2i370e8awd6sr">{chat.initials}</span>
              <RowText title={chat.name} meta={chat.when} data-id="element-3878" data-mp="28512s2w4imtp" />
              <span className="shrink-0 font-mono text-[11px] text-app-subtle" data-id="element-3879" data-mp="286kdzf2xa7om">{chat.ref}</span>
            </Row>)}
        </Panel>

        <h3 className="mb-2.5 mt-7 text-[13px] font-semibold text-app-muted" data-id="element-3880" data-mp="2d1ri2o03tee2">Linked</h3>
        <Panel data-id="element-3881" data-mp="30rloontty6pv">
          {context.map(item => <Row key={item.title} data-id="element-3882" data-mp="35tb38dg9h2ip">
              <Glyph icon={FileTextIcon} data-id="element-3883" data-mp="35s9g3alz0pkm" />
              <RowText title={item.title} meta={item.meta} data-id="element-3884" data-mp="2qvipnqu9yo75" />
            </Row>)}
          {primaryChannel && <Row data-id="element-3885" data-mp="2vuli8bt2j1y8">
              <Glyph icon={HashIcon} data-id="element-3886" data-mp="2vu2blr8vbzzi" />
              <RowText title={primaryChannel.title} meta={primaryChannel.meta} data-id="element-3887" data-mp="2vt0qety51x42" />
            </Row>}
        </Panel>
      </section>
    </div>;
}
export function TrackLinkedView({
  trackName
}: TrackDetailViewProps) {
  const inventory = linkedFor(trackName);
  return <div className="mx-auto grid w-full max-w-[1040px] grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-3888" data-mp="2hab6579qpap">
      <Panel data-id="element-3889" data-mp="3tod1kufa8rjv">{inventory.left.map(category => <LinkedCategoryRows key={category.id} category={category} data-id="element-3890" data-mp="3tnbgry8gsqoy" />)}</Panel>
      <Panel data-id="element-3891" data-mp="biroxgx7eew8">{inventory.right.map(category => <LinkedCategoryRows key={category.id} category={category} data-id="element-3892" data-mp="bhoz7cbmaihw" />)}</Panel>
    </div>;
}
export function TrackActivityView({
  trackName
}: TrackDetailViewProps) {
  const days = activityFor(trackName);
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'All types' | ActivityTag>('All types');
  const [authors, setAuthors] = useState<Set<ActivityEntry['author']>>(() => new Set(['human', 'bot']));
  const q = query.trim().toLowerCase();
  const toggleAuthor = (author: ActivityEntry['author']) => setAuthors(current => {
    const next = new Set(current);
    if (next.has(author) && next.size > 1) next.delete(author);else next.add(author);
    return next;
  });
  const matches = (entry: ActivityEntry) => authors.has(entry.author) && (type === 'All types' || entry.tags.includes(type)) && (!q || entry.text.toLowerCase().includes(q));
  return <div className="mx-auto w-full max-w-[1040px]" data-id="element-3893" data-mp="104j3n7sa2ku4">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3" data-id="element-3894" data-mp="10382lfq7vr6p">
        <div className="w-full sm:w-[260px]" data-id="element-3895" data-mp="14ydviyi1e64a">
          <SearchField value={query} onChange={setQuery} placeholder="Search this track" label="Search activity" data-id="element-3896" data-mp="19vxbq3yaosyv" />
        </div>
        <div className="flex items-center gap-2" data-id="element-3897" data-mp="c8x3w328qt94">
          <SelectPill value={type} onChange={value => setType(value as typeof type)} options={['All types', 'Discussion', 'Decision', 'Issue', 'Progress', 'Customer signal', 'Metric']} label="Filter by event type" data-id="element-3898" data-mp="h6q7qpg2nb68" />
          <div className="inline-flex h-9 items-center gap-0.5 rounded-lg bg-app-subtle p-1" data-id="element-3899" data-mp="r5fky4x0w7g5">
            {([['human', 'Human', UserIcon], ['bot', 'Bot', BotIcon]] as const).map(([key, label, Icon]) => <button key={key} type="button" onClick={() => toggleAuthor(key)} aria-pressed={authors.has(key)} className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12.5px] font-medium transition-colors duration-200 ease-out ${authors.has(key) ? 'bg-app-surface text-app shadow-[0_1px_2px_rgba(20,20,24,0.08)]' : 'text-app-subtle hover:text-app'}`} data-id="element-3900" data-mp="22jt5p0whc6ry">
                <Icon size={12} data-id="element-3901" data-mp="3da7lb2x4dei1" />
                {label}
              </button>)}
          </div>
        </div>
      </div>

      {days.map(day => {
      const entries = day.entries.filter(matches);
      if (!entries.length) return null;
      return <div key={day.date} className="mb-7 last:mb-0" data-id="element-3902" data-mp="2zc9lr3ngjmml">
            <h3 className="mb-2.5 text-[13px] font-semibold text-app-muted" data-id="element-3903" data-mp="2zevsj12ixy3r">{day.date}</h3>
            <Panel data-id="element-3904" data-mp="2ufiv5me118mx">
              {entries.map((entry, index) => <Row key={`${day.date}-${entry.time}-${index}`} data-id="element-3905" data-mp="2pf4y0zdyrj3o" data-mpi={index}>
                  <Glyph icon={entry.author === 'bot' ? BotIcon : UserIcon} data-id="element-3906" data-mp="sm1oom1mudtr" />
                  <span className="shrink-0 font-mono text-[12px] text-app-subtle" data-id="element-3907" data-mp="sjz1e9rv6ysq">{entry.time}</span>
                  <RowText title={entry.text} meta={entry.author === 'bot' ? 'Giga' : 'Team member'} data-id="element-3908" data-mp="nkvtmhki2eh2" />
                  <span className="hidden shrink-0 items-center gap-2.5 sm:flex" data-id="element-3909" data-mp="3sp3excxiguzm">
                    {entry.tags.map(tag => <TagLegend key={tag} tag={tag} data-id="element-3910" data-mp="1ijwbblfo0qr" />)}
                  </span>
                </Row>)}
            </Panel>
          </div>;
    })}
      {days.every(day => !day.entries.some(matches)) && <EmptyState data-id="element-3911" data-mp="3nu7b7fiwwj6o">Nothing here matches those filters.</EmptyState>}
    </div>;
}
function LinkedCategoryRows({
  category
}: {
  category: LinkedCategory;
}) {
  const Icon = CATEGORY_ICON[category.id] ?? Link2Icon;
  return <>
      <div className="flex items-center justify-between border-b border-app-divider px-4 py-2.5" data-id="element-3912" data-mp="2zyl4tryddtcp">
        <p className="flex items-center gap-2 text-[12.5px] font-semibold text-app-muted" data-id="element-3913" data-mp="34x667upcyj2q">
          <Icon size={13} strokeWidth={1.9} className="text-app-subtle" data-id="element-3914" data-mp="y6ym0domt4rb" />
          {category.name} · {category.items.length}
        </p>
        <button type="button" aria-label={`Add ${category.name}`} className="flex size-6 items-center justify-center rounded-md text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-3915" data-mp="13504awe8h01q">
          <PlusIcon size={13} data-id="element-3916" data-mp="2iqllo0ol12e3" />
        </button>
      </div>
      {category.items.length ? category.items.map((item, index) => <Row key={`${item.title}-${index}`} data-id="element-3917" data-mp="2xfzy5hngfzxp" data-mpi={index}>
            <RowText title={item.title} meta={item.meta} data-id="element-3918" data-mp="2smwz4tzwrya4" />
            {item.badges?.map(badge => <span key={badge} className="shrink-0 rounded-md bg-app-subtle px-2 py-0.5 text-[11px] font-medium text-app-muted" data-id="element-3919" data-mp="36e24mcpyjksd">{badge}</span>)}
          </Row>) : <p className="border-b border-app-divider px-4 py-3.5 text-[13px] text-app-subtle last:border-0" data-id="element-3920" data-mp="32cqc9zfp8lgh">No {category.name.toLowerCase()} linked yet.</p>}
    </>;
}
function TagLegend({
  tag
}: {
  tag?: ActivityTag;
}) {
  if (!tag) return null;
  return <span className="inline-flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-app-muted" data-id="element-3921" data-mp="1lyi3r8rrkcnu">
      <span className="size-1.5 rounded-full" style={{
      backgroundColor: TAG_DOT[tag]
    }} data-id="element-3922" data-mp="1h06p8kqixdza" />
      {tag}
    </span>;
}